import {
  MlAllergenPrediction,
  MlAnalysisResponse,
  MlIngredientAnalysisResult,
} from "@/models/AllergenML/AllergenML";
import { NutrientSource } from "@/models/NutrientSource/NutrientSource";
import { formatCountrySpecificAllergenName } from "@/utils/AllergenDisplayNames";

export type MatrixColumn = {
  key: string;
  allergenCode: string;
  label: string;
  displayLabel?: string;
  iconClass: string;
  colorClass: string;
  isOptionalAllergen?: boolean;
  isFirstOptionalAllergen?: boolean;
  optionalGroupKey?: string;
};

export type MatrixCellState = {
  status: "ai" | "user-added" | "user-confirmed" | "user-removed";
  iconClass: string;
  accentIconClass?: string;
  badgeClass: string;
  tooltip: string;
  isPresent: boolean;
};

export type MatrixRow = {
  key: string;
  ingredientId?: number | null;
  ingredient: string;
  flagged: string[];
  flaggedAllergenCodes: Record<string, string[]>;
  cellStates: Record<string, MatrixCellState>;
};

export type MatrixTranslateFn = (key: string, fallback: string, ...args: Array<string | number>) => string;

export const matrixColumns: MatrixColumn[] = [
  { key: "crustaceans", allergenCode: "CRUSTACEANS", label: "Crustaceans", iconClass: "allergen-symbol allergen-symbol-crustaceans", colorClass: "crustaceans" },
  { key: "egg", allergenCode: "EGG", label: "Eggs", iconClass: "allergen-symbol allergen-symbol-egg", colorClass: "egg" },
  { key: "fish", allergenCode: "FISH", label: "Fish", iconClass: "allergen-symbol allergen-symbol-fish", colorClass: "fish" },
  { key: "milk", allergenCode: "MILK", label: "Milk", iconClass: "allergen-symbol allergen-symbol-milk", colorClass: "milk" },
  { key: "molluscs", allergenCode: "MOLLUSCS", label: "Molluscs", iconClass: "allergen-symbol allergen-symbol-molluscs", colorClass: "molluscs" },
  { key: "treeNuts", allergenCode: "TREE_NUTS", label: "Nuts/Tree Nuts", displayLabel: "Tree nuts", iconClass: "allergen-symbol allergen-symbol-nuts", colorClass: "treeNuts" },
  { key: "peanuts", allergenCode: "PEANUT", label: "Peanuts", iconClass: "allergen-symbol allergen-symbol-peanut", colorClass: "peanuts" },
  { key: "sesame", allergenCode: "SESAME_SEED", label: "Sesame Seeds", displayLabel: "Sesame", iconClass: "allergen-symbol allergen-symbol-sesame", colorClass: "sesame" },
  { key: "soy", allergenCode: "SOY", label: "Soy/Soybean", displayLabel: "Soya", iconClass: "allergen-symbol allergen-symbol-soy", colorClass: "soy" },
  { key: "sulphites", allergenCode: "SULPHITES", label: "Sulphites", iconClass: "allergen-symbol allergen-symbol-sulphites", colorClass: "sulphites" },
  { key: "wheat", allergenCode: "WHEAT", label: "Wheat/Triticale", displayLabel: "Wheat", iconClass: "allergen-symbol allergen-symbol-wheat", colorClass: "wheat" },
  { key: "celery", allergenCode: "CELERY", label: "Celery", iconClass: "allergen-symbol allergen-symbol-celery", colorClass: "celery" },
  { key: "gluten", allergenCode: "GLUTEN", label: "Cereals containing Gluten", displayLabel: "Gluten", iconClass: "allergen-symbol allergen-symbol-wheat", colorClass: "gluten" },
  { key: "lupin", allergenCode: "LUPIN", label: "Lupin", iconClass: "allergen-symbol allergen-symbol-lupin", colorClass: "lupin" },
  { key: "mustard", allergenCode: "MUSTARD", label: "Mustard", iconClass: "allergen-symbol allergen-symbol-mustard", colorClass: "mustard" },
];

function formatAllergenName(value: string) {
  return (value || "").split("/")[0].trim();
}

export function normalizeAllergenKey(value: string) {
  const normalized = (value || "").toLowerCase().replace(/[^a-z]/g, "");

  switch (normalized) {
  case "celery":
    return "celery";
  case "milk":
  case "dairy":
    return "milk";
  case "egg":
  case "eggs":
    return "egg";
  case "fish":
    return "fish";
  case "crustaceans":
  case "crustacean":
    return "crustaceans";
  case "molluscs":
  case "mollusks":
    return "molluscs";
  case "shellfish":
  case "crustaceansandmolluscs":
  case "crustaceansandmollusks":
    return "crustaceans";
  case "lupin":
    return "lupin";
  case "treenut":
  case "treenuts":
  case "nuts":
  case "treenutsnuts":
  case "nutstreenuts":
    return "treeNuts";
  case "peanut":
  case "peanuts":
    return "peanuts";
  case "gluten":
  case "cerealscontaininggluten":
  case "cerealsthatcontaingluten":
    return "gluten";
  case "wheat":
  case "triticale":
  case "wheatandtriticale":
  case "wheattriticale":
    return "wheat";
  case "soy":
  case "soya":
  case "soybean":
  case "soybeans":
  case "soysoybean":
  case "soysoyasoybeans":
  case "soyasoybeans":
  case "soysoya":
    return "soy";
  case "sesame":
  case "sesameseed":
  case "sesameseeds":
    return "sesame";
  case "mustard":
    return "mustard";
  case "sulphites":
  case "sulfites":
  case "sulphurdioxideandsulphites":
  case "sulfurdioxideandsulfites":
    return "sulphites";
  default:
    return "";
  }
}

export function resolvePresentationAllergenKey(allergenId: string, displayName: string) {
  return normalizeAllergenKey(displayName || "")
    || normalizeAllergenKey(allergenId || "");
}

export function isUserOverridePrediction(allergen: { source?: string | null; primaryEvidenceType?: string | null; evidence?: string[] | null }) {
  const source = (allergen.source || "").toLowerCase();
  const evidenceType = (allergen.primaryEvidenceType || "").toLowerCase();
  const evidenceText = (allergen.evidence || []).join(" ").toLowerCase();

  return source.indexOf("override") !== -1
    || evidenceType === "override"
    || evidenceText.indexOf("manual") !== -1;
}

export function buildMatrixCellState(
  key: string,
  label: string,
  status: MatrixCellState["status"],
  country: NutrientSource,
  t: MatrixTranslateFn,
  columns: MatrixColumn[] = matrixColumns,
): MatrixCellState {
  const fallbackAllergenLabel = t("allergens_ai_this_allergen", "this allergen");
  const displayLabel = formatCountrySpecificAllergenName(formatAllergenName(
    label || columns.find((column) => column.key === key)?.label || fallbackAllergenLabel
  ), country);

  switch (status) {
  case "user-added":
    return {
      status,
      iconClass: "fa fa-check",
      accentIconClass: "fa fa-question",
      badgeClass: "allergen-badge-user",
      tooltip: t("allergens_ai_tooltip_user_added", `${displayLabel}: manually added as an allergen.`, displayLabel),
      isPresent: true,
    };
  case "user-confirmed":
    return {
      status,
      iconClass: "fa fa-check",
      badgeClass: "allergen-badge-user",
      tooltip: t("allergens_ai_tooltip_ai_detected", `${displayLabel}: AI detected this allergen.`, displayLabel),
      isPresent: true,
    };
  case "user-removed":
    return {
      status,
      iconClass: "fa fa-times",
      badgeClass: "allergen-badge-removed",
      tooltip: t("allergens_ai_tooltip_user_removed", `${displayLabel}: manually marked as not an allergen.`, displayLabel),
      isPresent: false,
    };
  case "ai":
  default:
    return {
      status: "ai",
      iconClass: "fa fa-check",
      badgeClass: "allergen-badge-ai",
      tooltip: t("allergens_ai_tooltip_detected_by_ai", `${displayLabel}: detected by AI as an allergen.`, displayLabel),
      isPresent: true,
    };
  }
}

export function matrixRowDedupeKey(row: Pick<MatrixRow, "ingredientId" | "ingredient">) {
  return row.ingredientId
    ? `ingredient-${row.ingredientId}`
    : `ingredient-name-${(row.ingredient || "").trim().toLowerCase()}`;
}

export function copyFlaggedAllergenCodes(flaggedAllergenCodes: Record<string, string[]>) {
  return Object.keys(flaggedAllergenCodes).reduce((copied, allergenKey) => {
    copied[allergenKey] = [...flaggedAllergenCodes[allergenKey]];
    return copied;
  }, {} as Record<string, string[]>);
}

export function mergeUniqueValues(target: string[], values: string[]) {
  values.forEach((value) => {
    if (target.indexOf(value) === -1) {
      target.push(value);
    }
  });
}

export function matrixCellStatePriority(state: MatrixCellState) {
  switch (state.status) {
  case "user-removed":
    return 4;
  case "user-confirmed":
    return 3;
  case "user-added":
    return 2;
  default:
    return 1;
  }
}

export function mergeDuplicateMatrixRows(
  rows: MatrixRow[],
  getRowOrder: (row: MatrixRow) => number,
) {
  const mergedRows: MatrixRow[] = [];
  const mergedRowsByKey: Record<string, MatrixRow> = {};

  rows.forEach((row) => {
    const dedupeKey = matrixRowDedupeKey(row);
    const existingRow = mergedRowsByKey[dedupeKey];

    if (!existingRow) {
      const copiedRow: MatrixRow = {
        ...row,
        flagged: [...row.flagged],
        flaggedAllergenCodes: copyFlaggedAllergenCodes(row.flaggedAllergenCodes),
        cellStates: { ...row.cellStates },
      };

      mergedRowsByKey[dedupeKey] = copiedRow;
      mergedRows.push(copiedRow);
      return;
    }

    mergeUniqueValues(existingRow.flagged, row.flagged);

    Object.keys(row.flaggedAllergenCodes).forEach((allergenKey) => {
      if (!existingRow.flaggedAllergenCodes[allergenKey]) {
        existingRow.flaggedAllergenCodes[allergenKey] = [];
      }

      mergeUniqueValues(existingRow.flaggedAllergenCodes[allergenKey], row.flaggedAllergenCodes[allergenKey]);
    });

    Object.keys(row.cellStates).forEach((allergenKey) => {
      const currentState = existingRow.cellStates[allergenKey];
      const nextState = row.cellStates[allergenKey];

      if (!currentState || matrixCellStatePriority(nextState) > matrixCellStatePriority(currentState)) {
        existingRow.cellStates[allergenKey] = nextState;
      }
    });
  });

  return mergedRows.sort((left, right) => getRowOrder(left) - getRowOrder(right));
}

function buildMatrixRow(
  ingredient: MlIngredientAnalysisResult,
  country: NutrientSource,
  t: MatrixTranslateFn,
  normalizeCountryPresentationAllergenKey: (allergenKey: string) => string,
) {
  const flaggedAllergenCodes: Record<string, string[]> = {};
  const cellStates: Record<string, MatrixCellState> = {};

  ingredient.allergens.forEach((allergen) => {
    const normalizedKey = normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(
      allergen.id || "",
      allergen.displayName || ""
    ));

    if (!normalizedKey) {
      return;
    }

    const allergenCode = allergen.id || allergen.displayName || normalizedKey;
    if (!flaggedAllergenCodes[normalizedKey]) {
      flaggedAllergenCodes[normalizedKey] = [];
    }

    if (flaggedAllergenCodes[normalizedKey].indexOf(allergenCode) === -1) {
      flaggedAllergenCodes[normalizedKey].push(allergenCode);
    }

    cellStates[normalizedKey] = buildMatrixCellState(
      normalizedKey,
      allergen.displayName || allergen.id || normalizedKey,
      isUserOverridePrediction(allergen) ? "user-added" : "ai",
      country,
      t,
    );
  });

  (ingredient.overrideMarkers || []).forEach((marker) => {
    const normalizedKey = normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(
      marker.allergenCode || "",
      marker.displayName || ""
    ));

    if (!normalizedKey) {
      return;
    }

    const allergenCode = marker.allergenCode || marker.displayName || normalizedKey;
    if (!flaggedAllergenCodes[normalizedKey]) {
      flaggedAllergenCodes[normalizedKey] = [];
    }

    if (flaggedAllergenCodes[normalizedKey].indexOf(allergenCode) === -1) {
      flaggedAllergenCodes[normalizedKey].push(allergenCode);
    }

    if (!marker.isAllergen) {
      flaggedAllergenCodes[normalizedKey] = flaggedAllergenCodes[normalizedKey]
        .filter((code) => code !== allergenCode);
    }

    cellStates[normalizedKey] = buildMatrixCellState(
      normalizedKey,
      marker.displayName || marker.allergenCode || normalizedKey,
      marker.isAllergen
        ? marker.wasAiDetected ? "user-confirmed" : "user-added"
        : "user-removed",
      country,
      t,
    );
  });

  return {
    key: `${ingredient.ingredientId || ingredient.ingredientName}-${ingredient.lineagePath || ""}`,
    ingredientId: ingredient.ingredientId,
    ingredient: ingredient.ingredientName,
    flagged: Object.keys(flaggedAllergenCodes).filter((key) => flaggedAllergenCodes[key].length > 0),
    flaggedAllergenCodes,
    cellStates,
  } satisfies MatrixRow;
}

export function buildMatrixRows(
  analysisResult: MlAnalysisResponse | null,
  country: NutrientSource,
  t: MatrixTranslateFn,
  normalizeCountryPresentationAllergenKey: (allergenKey: string) => string,
  getRowOrder: (row: MatrixRow) => number,
) {
  if (!analysisResult) {
    return [];
  }

  return mergeDuplicateMatrixRows(
    analysisResult.ingredients.map((ingredient) => buildMatrixRow(ingredient, country, t, normalizeCountryPresentationAllergenKey)),
    getRowOrder,
  );
}

export function createAnalysisResultForMatrixRows(ingredients: MlIngredientAnalysisResult[]): MlAnalysisResponse {
  return {
    restaurantId: 1,
    targetType: "ingredient",
    targetId: 1,
    nutrientSource: NutrientSource.US,
    allergens: [],
    ingredients,
    warnings: [],
    generatedOnUtc: "2026-01-01T00:00:00Z",
  };
}

export function createMlAllergenPrediction(overrides: Partial<MlAllergenPrediction> = {}): MlAllergenPrediction {
  return {
    id: "MILK",
    score: 1,
    confidence: 1,
    confidenceBand: "high",
    evidence: ["milk"],
    source: "analysis",
    ...overrides,
  };
}